import React, { useState, useMemo, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { i18next, dialogs, site, integration, anodum, template } from '@yola/ws-sdk';
import { reactHookForm } from '@yola/ws-ui';
import useFeatureFlags from 'yola-editor/src/js/modules/feature-flags/hooks/use-feature-flags';
import upsell from 'src/js/modules/upsell';
import segment from 'src/js/modules/analytics/segment';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import getLanguageCodes from 'src/js/modules/website-settings/helpers/get-language-codes';
import yolaSite from 'yola-editor/src/js/modules/site';
import PageSettings from '../components/page-settings';
import useScrollToError from '../hooks/use-scroll-to-error';
import getKeywordsString from '../helpers/get-keywords-string';
import getKeywordsList from '../helpers/get-keywords-list';
import trimLocaleSuffixFromPath from '../helpers/trim-locale-suffix-from-path';
import appendLocaleSuffixToPath from '../helpers/append-locale-suffix-to-path';
import getLanguageCodesOptions from '../helpers/get-language-codes-options';
import getPathPrefix from '../helpers/get-path-prefix';
import getFieldNamesByTabId from '../helpers/get-field-names-by-tab-id';
import getTabIdByFieldName from '../helpers/get-tab-id-by-field-name';
import constants from '../constants';

const { useForm, useFormState } = reactHookForm;

const {
  trackers: { trackDialogActiveTabChanged, trackUpgradeTriggerClicked, trackCodeEditorFocused },
  constants: { triggerIds },
} = segment;
const { common, tabIdentifiers, fieldNames } = constants;

const getPageSettingsCaptions = ({ customCodeUpgradeText = {} }) => ({
  apply: i18next.t('Submit'),
  cancel: i18next.t('Cancel'),
  pageSettings: i18next.t('Page Settings'),
  general: {
    tabLabel: i18next.t('General'),
    pageName: {
      name: i18next.t('Page name'),
    },
    displayInNavigation: {
      label: i18next.t('Display in navigation'),
    },
    pageLocale: {
      name: i18next.t('Language Code'),
      description: i18next.t(
        'Set page language code to allow browsers, translation apps, and other tools to perform language-sensitive tasks.'
      ),
      placeholder: i18next.t('Select...'),
    },
    passwordProtectedPages: {
      title: i18next.t('Page access control'),
      subtitle: i18next.t('Manage the access to the content of your page'),
      openedOptionLabel: i18next.t('Open for everyone'),
      protectedOptionLabel: i18next.t('Password protected'),
      fieldTitle: i18next.t('Password'),
      fieldPlaceholder: i18next.t('Enter password for this page'),
      fieldSubtitle: i18next.t(
        'Set a password to ensure that only authorized visitors can view this page. The settings apply after publishing the website.'
      ),
    },
  },
  seoSettings: {
    tabLabel: i18next.t('SEO'),
    searchPreview: {
      heading: i18next.t('Search results preview'),
      title: i18next.t('Your page title'),
      description: i18next.t(
        'Search engines will create a default description for your page, but to override that description, type your own below. This is what viewers see in search results, so use it to show what makes your page special!'
      ),
    },
    title: {
      name: i18next.t('Title tag'),
      description: i18next.t(
        'This is your page title on Google. Add your page name, site name & what you do.'
      ),
    },
    description: {
      name: i18next.t('Meta description'),
      description: i18next.t(
        'Add a brief summary of your page content. If left blank, search engines may use your page content instead.'
      ),
    },
    preventIndexing: {
      name: i18next.t('Hide this page from search results'),
      description: i18next.t(
        'Prevent your page from being indexed by search engines to hide it in Google search'
      ),
    },
    keywords: {
      name: i18next.t('Meta keywords'),
      description: i18next.t(
        'Add page meta keywords. Optimal description length is 155 to 300 characters.'
      ),
    },
    sameAsPageName: {
      label: i18next.t('Same as page name'),
    },
    displayBusinessName: {
      label: i18next.t('Include your Business Name in the page title'),
      description: i18next.t(
        'To use the Business Name in the page title, you need to add your business name in {pathToBusinessInfo}',
        {
          pathToBusinessInfo: '',
        }
      ),
      settingsTitle: i18next.t('Settings'),
      businessInfoTitle: i18next.t('Business Info'),
    },
  },
  customCode: {
    tabLabel: i18next.t('Custom code'),
    description: i18next.t(
      'Tweak the site by adding custom code, and integration snippets. The code added here will only apply to this page and will appear after the site-wide custom code.'
    ),
    headCode: {
      name: i18next.t('Inside <head> tag'),
      description: i18next.t(
        'Custom code and scripts added to this section will appear in the preview mode and on the published site.'
      ),
      upgradeText: {
        description: i18next.t(
          'Gain access to custom code editing by upgrading your subscription!'
        ),
        upgrade: i18next.t('Upgrade now'),
        ...customCodeUpgradeText,
      },
    },
    headerCode: {
      name: i18next.t('After <body> tag'),
      description: i18next.t(
        'Custom code and scripts added to this section will appear in the preview mode and on the published site.'
      ),
      upgradeText: {
        description: i18next.t(
          'Gain access to custom code editing by upgrading your subscription!'
        ),
        upgrade: i18next.t('Upgrade now'),
        ...customCodeUpgradeText,
      },
    },
    footerCode: {
      name: i18next.t('Before </body> tag'),
      description: i18next.t(
        'Custom code and scripts added to this section will appear in the preview mode and on the published site.'
      ),
      upgradeText: {
        description: i18next.t(
          'Gain access to custom code editing by upgrading your subscription!'
        ),
        upgrade: i18next.t('Upgrade now'),
        ...customCodeUpgradeText,
      },
    },
  },
  confirmExit: {
    title: i18next.t('Save changes?'),
    description: i18next.t('Do you want to save changes you made in page settings?'),
    discard: i18next.t('Discard'),
    submit: i18next.t('Save'),
    cancel: i18next.t('Cancel'),
  },
});

const PageSettingsContainer = ({
  onDialogMainAction,
  onDialogCancel,
  pageId,
  activeTabId: tabId = tabIdentifiers.GENERAL,
}) => {
  const dispatch = useDispatch();
  const userLimits = useSelector(integration.selectors.getLimits);
  const upsells = useSelector(integration.selectors.getUpsells);
  const siteURL = useSelector(yolaSite.selectors.getSiteURL);
  const pageSettings = useSelector((state) => site.selectors.getPage(state, pageId));
  const siteSettings = useSelector(site.selectors.getSettings);

  const captions = getPageSettingsCaptions({
    ...(upsells.customCode && { customCodeUpgradeText: upsells.customCode.captions }),
  });
  const languageCodes = useMemo(() => getLanguageCodes(), []);
  const languageCodeOptions = useMemo(
    () => getLanguageCodesOptions(languageCodes),
    [languageCodes]
  );
  const isMultilingualSite = site.verifiers.isMultilingualSite();
  const isDisplayInNavigationAvailable =
    template.verifiers.isVariableGlobalsAvailable('navigation');
  const isPasswordProtectedPagesFeatureAvailable = upsell.verifiers.isFeatureAvailable(
    upsell.features.PASSWORD_PROTECTED_PAGES
  );
  const isHomePage = site.verifiers.isHomePage(pageSettings.path, languageCodes);
  const isCustomCodeUserLimitAvailable = userLimits.customCode.available;

  const [activeTabId, setActiveTabId] = useState(tabId);
  const [shouldScrollToError, setShouldScrollToError] = useState(false);
  const [passwordProtection, setPasswordProtection] = useState(Boolean(pageSettings.password));

  const [featureFlags] = useFeatureFlags([
    'business_info_in_website_settings',
    'password_protected_pages',
  ]);
  const {
    business_info_in_website_settings: isBusinessInfoInWebsiteSettingsFeatureFlagEnabled,
    password_protected_pages: isPasswordProtectedPagesFeatureFlagEnabled,
  } = featureFlags;

  const initialName = pageSettings.name || pageSettings.title;
  const initialPath = isMultilingualSite
    ? trimLocaleSuffixFromPath(pageSettings.path)
    : pageSettings.path;
  const initialPassword = pageSettings.password || '';
  const initialSameAsPageName =
    pageSettings.sameAsPageName !== undefined ? pageSettings.sameAsPageName : true;
  const initialPageTitle =
    initialSameAsPageName && !!pageSettings.name ? pageSettings.name : pageSettings.title;
  const initialDisplayBusinessName = Boolean(
    pageSettings.displayBusinessName && siteSettings.businessName
  );
  const initialPreventIndexing = pageSettings.preventIndexing || false;
  const initialDescription = pageSettings.description || '';
  const initialKeywords = getKeywordsList(pageSettings.keywords);

  const {
    control,
    getValues,
    setValue,
    trigger: triggerValidation,
  } = useForm({
    mode: 'onTouched',
    defaultValues: {
      [fieldNames.NAME]: initialName,
      [fieldNames.PATH]: initialPath,
      [fieldNames.HTML_LOCALE]: pageSettings.htmlLocale,
      [fieldNames.DISPLAY_IN_NAVIGATION]: pageSettings.displayInNavigation,
      ...(isPasswordProtectedPagesFeatureFlagEnabled &&
        passwordProtection && {
          [fieldNames.PASSWORD]: initialPassword,
        }),
      [fieldNames.TITLE]: initialPageTitle,
      [fieldNames.SAME_AS_PAGE_NAME]: initialSameAsPageName,
      ...(isBusinessInfoInWebsiteSettingsFeatureFlagEnabled && {
        [fieldNames.DISPLAY_BUSINESS_NAME]: initialDisplayBusinessName,
      }),
      [fieldNames.PREVENT_INDEXING]: initialPreventIndexing,
      [fieldNames.DESCRIPTION]: initialDescription,
      [fieldNames.KEYWORDS]: initialKeywords,
      [fieldNames.HEAD_CODE]: pageSettings.headCode,
      [fieldNames.HEADER_CODE]: pageSettings.headerCode,
      [fieldNames.FOOTER_CODE]: pageSettings.footerCode,
    },
  });
  const { dirtyFields, errors } = useFormState({
    control,
  });

  const { scrollToError, fieldRefs } = useScrollToError({
    control,
    activeTabId,
  });

  const setActiveTabIdWithError = (fieldNamesWithErrors) => {
    const isActiveTabContainsFieldNameWithError = fieldNamesWithErrors.some((fieldName) =>
      getFieldNamesByTabId(activeTabId).includes(fieldName)
    );
    const tabIdWithError = isActiveTabContainsFieldNameWithError
      ? activeTabId
      : getTabIdByFieldName(fieldNamesWithErrors[0]);

    setActiveTabId(tabIdWithError);
    setShouldScrollToError(true);
  };

  const handleSubmit = async () => {
    await triggerValidation();

    const fieldNamesWithErrors = Object.keys(errors);

    if (fieldNamesWithErrors.length) {
      setActiveTabIdWithError(fieldNamesWithErrors);
      return;
    }

    const settings = getValues();
    const displayInNavigation = isDisplayInNavigationAvailable
      ? settings.displayInNavigation
      : pageSettings.displayInNavigation;
    const password = passwordProtection && settings.password ? settings.password : null;
    const path = isMultilingualSite
      ? appendLocaleSuffixToPath(settings.path, pageSettings.locale)
      : settings.path;
    const keywords = getKeywordsString(settings.keywords);

    const preparedSettings = {
      ...settings,
      displayInNavigation,
      password,
      path,
      keywords,
    };

    onDialogMainAction(preparedSettings);
    dispatch(site.actions.updatePageSettings(pageId, preparedSettings));

    dialogs.operations.hide();
  };

  const handleCancel = () => {
    onDialogCancel();
    dispatch(dialogs.actions.show(dialogTypes.PAGE_MANAGER));
  };

  const handleTabChange = (newTabId) => {
    setActiveTabId(newTabId);
    trackDialogActiveTabChanged({ dialogId: dialogTypes.PAGE_MANAGER, tabId: newTabId });
  };

  const handleEnablePasswordProtection = () => {
    if (!isPasswordProtectedPagesFeatureAvailable) {
      upsells.passwordProtectedPages
        .onUpgrade()
        .then(() => {
          setPasswordProtection(true);
        })
        .catch(() => {
          // eslint-disable-next-line no-console
          console.log('Upgrade flow was canceled');
        });

      return;
    }

    setPasswordProtection(true);
  };

  const handleDisablePasswordProtection = () => {
    setPasswordProtection(false);
  };

  const handleChangePageName = () => {
    const [shouldBeSameAsPageName, pageName] = getValues([
      fieldNames.SAME_AS_PAGE_NAME,
      fieldNames.NAME,
    ]);

    if (shouldBeSameAsPageName) {
      setValue(fieldNames.TITLE, pageName);
    }
  };

  const handleChangeSameAsPageName = () => {
    const [shouldBeSameAsPageName, pageName] = getValues([
      fieldNames.SAME_AS_PAGE_NAME,
      fieldNames.NAME,
    ]);

    if (shouldBeSameAsPageName) {
      setValue(fieldNames.TITLE, pageName, { shouldValidate: true });
    }
  };

  const handleCodeEditorFocus = () => {
    trackCodeEditorFocused(dialogTypes.PAGE_SETTINGS);
  };

  const handleCustomCodeUpgrade = () => {
    trackUpgradeTriggerClicked(triggerIds.PAGE_SETTINGS);

    return upsells.customCode.onUpgrade().catch(() => {
      // eslint-disable-next-line no-console
      console.log('Upgrade flow was canceled');
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === common.ENTER_KEY) {
      if (anodum.isOneOfTags(e.target, ['input', 'textarea'])) return;
      handleSubmit();
    }
  };

  useLayoutEffect(() => {
    if (shouldScrollToError) {
      scrollToError(activeTabId);
      setShouldScrollToError(false);
    }
  }, [activeTabId, scrollToError, shouldScrollToError]);

  return (
    <PageSettings
      activeTabId={activeTabId}
      control={control}
      fieldRefs={fieldRefs}
      captions={captions}
      pageId={pageId}
      pathPrefix={getPathPrefix(siteURL, pageSettings.locale)}
      businessName={siteSettings.businessName || ''}
      languageCodeOptions={languageCodeOptions}
      isHomePage={isHomePage}
      isDisplayInNavigationAvailable={isDisplayInNavigationAvailable}
      isPasswordProtected={passwordProtection}
      isPasswordProtectedPagesFeatureAvailable={isPasswordProtectedPagesFeatureAvailable}
      isPasswordProtectedPagesFeatureFlagEnabled={Boolean(
        isPasswordProtectedPagesFeatureFlagEnabled
      )}
      isBusinessInfoInWebsiteSettingsFeatureFlagEnabled={
        isBusinessInfoInWebsiteSettingsFeatureFlagEnabled
      }
      isHeadCodeDisabled={!isCustomCodeUserLimitAvailable}
      isHeaderCodeDisabled={!isCustomCodeUserLimitAvailable}
      isFooterCodeDisabled={!isCustomCodeUserLimitAvailable}
      shouldConfirmExitDisplayed={Object.keys(dirtyFields).length > 0}
      onChangePageName={handleChangePageName}
      onChangeSameAsPageName={handleChangeSameAsPageName}
      onEnablePasswordProtection={handleEnablePasswordProtection}
      onDisablePasswordProtection={handleDisablePasswordProtection}
      onCodeEditorFocus={handleCodeEditorFocus}
      onHeadCodeUpgradeClick={handleCustomCodeUpgrade}
      onHeaderCodeUpgradeClick={handleCustomCodeUpgrade}
      onFooterCodeUpgradeClick={handleCustomCodeUpgrade}
      onTabChange={handleTabChange}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      onKeyDown={handleKeyDown}
      onConfirmExitSubmit={handleSubmit}
      onConfirmExitCancel={handleCancel}
    />
  );
};

PageSettingsContainer.propTypes = {
  pageId: PropTypes.string.isRequired,
  activeTabId: PropTypes.string,
  onDialogMainAction: PropTypes.func.isRequired,
  onDialogCancel: PropTypes.func.isRequired,
};

export default PageSettingsContainer;
